import React, { useState, useEffect, useMemo } from 'react';
import { getOrganizationalHierarchy, getMyHierarchy } from '../api/teamApi';
import { OrgHierarchySkeleton } from '../components/common/Skeleton';
import { PageFadeSection } from '../components/common/PageFadeSection';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../constants/userRole';

/* ─── helpers ─── */
const initials = (n) => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

const countAll = (nodes) => {
  let d = 0, t = 0, p = 0, pm = 0;
  const walk = (list) => list.forEach(n => {
    if (n.type === 'department') d++;
    else if (n.type === 'pm') pm++;
    else if (n.type === 'team') t++;
    else p++;
    if (n.children?.length) walk(n.children);
  });
  walk(nodes);
  return { d, t, p, pm };
};

/* ─────────────────────── Person Row ─────────────────────── */
const PersonRow = ({ person, isLead }) => (
  <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg group/person transition ${isLead ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
    <div className="relative">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-sm ${
        isLead
          ? 'bg-gradient-to-br from-amber-400 to-orange-500 ring-2 ring-amber-200/60 dark:ring-amber-700/60'
          : 'bg-gradient-to-br from-emerald-400 to-teal-500'
      }`}>
        {initials(person.name)}
      </div>
      {isLead && (
        <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-400 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800">
          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </div>
      )}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 truncate">{person.name}</p>
      {person.email && <p className="text-[10px] text-gray-400 dark:text-gray-400 truncate">{person.email}</p>}
    </div>
    {isLead && (
      <span className="text-[8px] font-extrabold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full uppercase tracking-widest">Lead</span>
    )}
  </div>
);

/* ─────────────────────── PM Card ─────────────────────── */
const PMCard = ({ pm }) => {
  const [open, setOpen] = useState(true);
  const teams = pm.children?.filter(c => c.type === 'team') || [];

  return (
    <div className="flex flex-col items-center">
      <button onClick={() => setOpen(o => !o)} className="focus:outline-none group/pm">
        <div className="relative bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 rounded-2xl px-6 py-4 shadow-lg shadow-amber-500/15 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 min-w-[240px]">
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center ring-1 ring-white/30">
              <span className="text-white font-bold text-sm">{initials(pm.name)}</span>
            </div>
            <div className="text-left">
              <h3 className="text-white font-bold text-base tracking-tight">{pm.name}</h3>
              <p className="text-amber-100 text-xs font-medium">Project Manager</p>
            </div>
            <svg className={`w-4 h-4 text-white/50 ml-auto transition-transform duration-300 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {open && teams.length > 0 && (
        <div className="flex flex-col items-center w-full">
          <Stem h={36} gradient />
          <div className="overflow-x-auto max-w-full pb-2 w-full">
            <div className="flex justify-center">
              <div className="inline-flex">
                {teams.map((team, i) => (
                  <div key={team.id} className="flex flex-col items-center relative" style={{ padding: '0 14px' }}>
                    {teams.length > 1 && (
                      <div className={`absolute top-0 h-[2px] bg-gray-200 dark:bg-gray-600 ${
                        i === 0 ? 'left-1/2 right-0'
                        : i === teams.length - 1 ? 'left-0 right-1/2'
                        : 'left-0 right-0'
                      }`} />
                    )}
                    <Stem h={24} />
                    <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 ring-[3px] ring-amber-100 dark:ring-amber-900/50 mb-2 flex-shrink-0" />
                    <TeamCard team={team} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Team Card ─────────────────────── */
const TeamCard = ({ team }) => {
  const leads = team.children?.filter(c => c.type === 'lead') || [];
  const members = team.children?.filter(c => c.type !== 'lead') || [];

  return (
    <div className="w-[260px] bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100/80 dark:border-gray-700 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group">
      {/* Top accent */}
      <div className="h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" />

      {/* Team header */}
      <div className="px-4 pt-3.5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm shadow-violet-200/50">
            <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-bold text-gray-900 dark:text-gray-100 text-sm truncate">{team.name}</h4>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">{team.memberCount ?? (leads.length + members.length)} people</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-dashed border-gray-100 dark:border-gray-700" />

      {/* People */}
      <div className="p-2 space-y-0.5 max-h-56 overflow-y-auto scrollbar-thin">
        {leads.map(l => <PersonRow key={l.id} person={l} isLead />)}
        {leads.length > 0 && members.length > 0 && <div className="mx-3 my-1 border-t border-gray-100 dark:border-gray-700" />}
        {members.map(m => <PersonRow key={m.id} person={m} />)}
        {leads.length === 0 && members.length === 0 && (
          <p className="text-xs text-gray-300 dark:text-gray-500 text-center py-4 italic">No members yet</p>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────── Connector Line ─────────────────────── */
const Stem = ({ h = 32, gradient }) => (
  <div className={gradient ? '' : 'bg-gray-200 dark:bg-gray-600'} style={{ width: 2, height: h, flexShrink: 0, alignSelf: 'center',
    ...(gradient ? { background: 'linear-gradient(180deg,#f59e0b 0%,#fcd34d 50%,#d1d5db 100%)' } : {}),
  }} />
);

/* ─────────────────────── Department / Org-Chart Node ─────────────────────── */
const DepartmentNode = ({ dept }) => {
  const [open, setOpen] = useState(true);
  const pmNodes = dept.children?.filter(c => c.type === 'pm') || [];
  const directTeams = dept.children?.filter(c => c.type === 'team') || [];
  const allChildren = [...pmNodes, ...directTeams];

  return (
    <div className="flex flex-col items-center">
      {/* ── department card ── */}
      <button onClick={() => setOpen(o => !o)} className="focus:outline-none group/dept">
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 rounded-2xl px-8 py-5 shadow-xl shadow-indigo-500/15 hover:shadow-2xl hover:shadow-indigo-500/25 hover:-translate-y-0.5 transition-all duration-300 min-w-[280px]">
          {/* subtle pattern overlay */}
          <div className="absolute inset-0 rounded-2xl opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
          />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center ring-1 ring-white/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-white font-extrabold text-lg tracking-tight capitalize">{dept.name}</h2>
              <p className="text-indigo-200 text-xs mt-0.5 font-medium">{allChildren.length} {pmNodes.length > 0 ? 'PM' : 'team'}{allChildren.length !== 1 ? 's' : ''}</p>
            </div>
            <svg className={`w-5 h-5 text-white/40 ml-auto transition-transform duration-300 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* ── tree branches to children (PMs and/or teams) ── */}
      {open && allChildren.length > 0 && (
        <div className="flex flex-col items-center w-full">
          {/* vertical stem down from department */}
          <Stem h={44} gradient />

          {/* children row with horizontal connectors */}
          <div className="overflow-x-auto max-w-full pb-2 w-full">
            <div className="flex justify-center">
              <div className="inline-flex">
                {allChildren.map((child, i) => (
                  <div key={child.id} className="flex flex-col items-center relative" style={{ padding: '0 14px' }}>
                    {/* horizontal connector segments */}
                    {allChildren.length > 1 && (
                      <div className={`absolute top-0 h-[2px] bg-gray-200 dark:bg-gray-600 ${
                        i === 0 ? 'left-1/2 right-0'
                        : i === allChildren.length - 1 ? 'left-0 right-1/2'
                        : 'left-0 right-0'
                      }`} />
                    )}

                    {/* vertical drop */}
                    <Stem h={28} />

                    {/* junction dot */}
                    <div className={`w-3 h-3 rounded-full ring-[3px] mb-3 flex-shrink-0 ${
                      child.type === 'pm'
                        ? 'bg-gradient-to-br from-amber-400 to-orange-500 ring-amber-100 dark:ring-amber-900/50'
                        : 'bg-gradient-to-br from-indigo-400 to-violet-500 ring-indigo-100 dark:ring-indigo-900/50'
                    }`} />

                    {/* PM card or team card */}
                    {child.type === 'pm' ? <PMCard pm={child} /> : <TeamCard team={child} />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {open && allChildren.length === 0 && (
        <p className="mt-6 text-sm text-gray-400 dark:text-gray-500 italic">No teams in this department</p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────── */
/*                         MAIN PAGE                              */
/* ─────────────────────────────────────────────────────────────── */
const OrgHierarchyPage = () => {
  const { user } = useAuth();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === UserRole.Admin;

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const result = await (isAdmin ? getOrganizationalHierarchy : getMyHierarchy)();
        result.isSuccess ? setHierarchy(result.data || []) : setError(result.errorMessage || 'Failed to load');
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  /* recursive search */
  const filtered = useMemo(() => {
    if (!search.trim()) return hierarchy;
    const q = search.toLowerCase();
    const dig = (nodes) => nodes.reduce((acc, n) => {
      const hit = [n.name, n.email, n.leadName].some(s => s?.toLowerCase().includes(q));
      const kids = n.children?.length ? dig(n.children) : [];
      if (hit || kids.length) acc.push({ ...n, children: kids.length ? kids : n.children });
      return acc;
    }, []);
    return dig(hierarchy);
  }, [hierarchy, search]);

  const stats = useMemo(() => countAll(hierarchy), [hierarchy]);

  /* ── loading ── */
  if (loading) return <OrgHierarchySkeleton />;

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-50/60 via-white to-violet-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-6 py-8">

        <PageFadeSection>
        {/* ────── Header ────── */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-10">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
              Organization
              <span className="ml-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500">Hierarchy</span>
            </h1>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1 font-medium">
              {isAdmin ? 'Full structure across departments' : 'Your teams & structure'}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* stat cards */}
            {[
              { label: 'Departments', v: stats.d, gradient: 'from-blue-500 to-indigo-600', light: 'bg-blue-50 text-blue-700' },
              { label: 'PMs', v: stats.pm, gradient: 'from-amber-500 to-orange-600', light: 'bg-amber-50 text-amber-700' },
              { label: 'Teams', v: stats.t, gradient: 'from-violet-500 to-purple-600', light: 'bg-violet-50 text-violet-700' },
              { label: 'People', v: stats.p, gradient: 'from-emerald-500 to-teal-600', light: 'bg-emerald-50 text-emerald-700' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur rounded-xl px-4 py-2.5 shadow-sm border border-gray-100/80 dark:border-gray-700">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white font-extrabold text-sm shadow-sm`}>
                  {s.v}
                </div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{s.label}</span>
              </div>
            ))}

            {/* search */}
            <div className="relative w-64">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full pl-10 pr-9 py-2.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-300/60 focus:border-indigo-300 transition shadow-sm dark:placeholder-gray-500"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          </div>
        </div>
        </PageFadeSection>

        <PageFadeSection>
        {/* ────── Error ────── */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-5 py-3.5 rounded-xl mb-8">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span className="text-sm font-medium">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
          </div>
        )}

        {/* ────── Org Chart ────── */}
        {filtered.length === 0 ? (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 p-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center mb-5">
              <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-semibold text-lg">{search ? 'No results found' : 'No organizational data'}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1.5">{search ? 'Try a different search term' : 'Create teams and add members to see the hierarchy'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto pb-12">
            <div className="inline-flex items-start gap-16 min-w-full justify-center">
              {filtered.map(dept => (
                <DepartmentNode key={dept.id} dept={dept} />
              ))}
            </div>
          </div>
        )}

        {/* ────── Legend ────── */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex items-center gap-6 bg-white/60 dark:bg-gray-800/60 backdrop-blur rounded-full px-6 py-2.5 border border-gray-100 dark:border-gray-700 shadow-sm">
            {[
              { color: 'bg-indigo-500', label: 'Department' },
              { color: 'bg-amber-500', label: 'PM' },
              { color: 'bg-violet-500', label: 'Team' },
              { color: 'bg-amber-400', label: 'Team Lead' },
              { color: 'bg-emerald-500', label: 'Member' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                {l.label}
              </div>
            ))}
          </div>
        </div>
        </PageFadeSection>
      </div>
    </div>
  );
};

export default OrgHierarchyPage;
